import {
   MAX_MEASUREMENT_ENTRIES_PER_REQUEST,
   measurementListResponseSchema,
   measurementUpsertResponseSchema,
   validateMeasurementEntry,
   type MeasurementEntry,
} from '@fasting-timer/shared'
import { safeParse } from 'valibot'

export type MeasurementUpsertOutcome = {
   entryId: string | null
   status: 'applied' | 'stale' | 'invalid'
   error?: string
}

type MeasurementListResult = { entries: MeasurementEntry[]; serverTime: string }
type MeasurementUpsertResult = MeasurementListResult & {
   results: MeasurementUpsertOutcome[]
}

type MeasurementsApiDeps = {
   measurementsEndpoint: string
   canUseRemoteSync: () => boolean
   getUserId: () => string
   resolveSyncUserId: () => Promise<string>
}

/**
 * Extracts an `error` message from a JSON error response, falling back to a default.
 *
 * @param response - The error response.
 * @param fallback - The message to use when none is present.
 * @returns The error message.
 */
export async function readErrorMessage(response: Response, fallback: string) {
   try {
      const payload: unknown = await response.json()
      const error = (payload as { error?: unknown } | null)?.error
      return typeof error === 'string' ? error : fallback
   } catch {
      return fallback
   }
}

/**
 * Normalizes server entries into the strict registry shape, dropping any the
 * client's registry no longer understands. Server timestamps are trusted, so the
 * future check is skipped.
 *
 * @param entries - Parsed transport entries.
 * @returns The strict entries.
 */
function toStrictEntries(entries: readonly unknown[]): MeasurementEntry[] {
   return entries.flatMap(entry => {
      const checked = validateMeasurementEntry(entry, { now: Number.MAX_SAFE_INTEGER })
      return checked.ok ? [checked.entry] : []
   })
}

/**
 * Creates the `/v1/measurements` client: delta listing and batch upsert. Deletes
 * travel as tombstones through the upsert, so no separate call is needed.
 *
 * @param deps - Injected endpoint and user/sync accessors.
 * @returns The API methods.
 */
export function createMeasurementsApi(deps: MeasurementsApiDeps) {
   /**
    * Returns whether a sync key is set and remote sync is currently usable.
    *
    * @returns True when remote calls are allowed.
    */
   function isRemoteAvailable() {
      return Boolean(deps.getUserId()) && deps.canUseRemoteSync()
   }

   /**
    * Lists entries, optionally only those updated after `since`.
    *
    * @param options - `since` ISO cursor from a previous response's `serverTime`.
    * @returns The strict entries and server time, or null when remote sync is unavailable.
    */
   async function list(options: { since?: string | null } = {}) {
      if (!isRemoteAvailable()) {
         return null
      }
      const hashedId = await deps.resolveSyncUserId()
      if (!hashedId) {
         return null
      }
      const params = new URLSearchParams({ userId: hashedId })
      if (options.since) {
         params.set('since', options.since)
      }
      const response = await fetch(`${deps.measurementsEndpoint}?${params.toString()}`)
      if (!response.ok) {
         throw new Error(
            await readErrorMessage(
               response,
               `Measurements fetch failed (${response.status})`,
            ),
         )
      }
      const parsed = safeParse(measurementListResponseSchema, await response.json())
      if (!parsed.success) {
         throw new Error('Measurements response was malformed.')
      }
      return {
         entries: toStrictEntries(parsed.output.entries),
         serverTime: parsed.output.serverTime,
      } satisfies MeasurementListResult
   }

   /**
    * Upserts entries in batches of the server's maximum request size.
    *
    * @param entries - The entries to push.
    * @returns The parsed responses (one per batch), or null when remote sync is unavailable.
    */
   async function upsert(entries: readonly MeasurementEntry[]) {
      if (!isRemoteAvailable() || entries.length === 0) {
         return null
      }
      const hashedId = await deps.resolveSyncUserId()
      if (!hashedId) {
         return null
      }
      const responses: MeasurementUpsertResult[] = []
      for (
         let index = 0;
         index < entries.length;
         index += MAX_MEASUREMENT_ENTRIES_PER_REQUEST
      ) {
         const batch = entries.slice(index, index + MAX_MEASUREMENT_ENTRIES_PER_REQUEST)
         const response = await fetch(deps.measurementsEndpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: hashedId, entries: batch }),
         })
         if (!response.ok) {
            throw new Error(
               await readErrorMessage(
                  response,
                  `Measurements push failed (${response.status})`,
               ),
            )
         }
         const parsed = safeParse(measurementUpsertResponseSchema, await response.json())
         if (!parsed.success) {
            throw new Error('Measurements push response was malformed.')
         }
         responses.push({
            results: parsed.output.results,
            entries: toStrictEntries(parsed.output.entries),
            serverTime: parsed.output.serverTime,
         })
      }
      return responses
   }

   return { list, upsert }
}

export type MeasurementsApi = ReturnType<typeof createMeasurementsApi>
